import React from "react";
import { motion } from "framer-motion";
import { ScrollText, Users2, Ban, Scale, AlertTriangle, Mail } from "lucide-react";
import { Link } from "react-router-dom";

import {
  TechLabel,
  StatusBadge,
  PageContainer,
  motionFadeUp,
  C,
} from "../../design-system";
import { createPageUrl } from "../../utils";

const sections = [
  {
    icon: ScrollText,
    title: "Using the service",
    body: [
      "By creating an account or using WebIdeal, you agree to these terms and to act in good faith, no scraping, no abuse, no automated misuse of AI features.",
      "You're responsible for keeping your credentials safe and for what your team does inside your workspace.",
      "We may update the service, fix bugs and ship features without prior notice, but we won't materially reduce paid features mid-term.",
    ],
  },
  {
    icon: Users2,
    title: "Your content, your data",
    body: [
      "You own your content. We need a limited license only to host, process and display it for the purpose of providing the service.",
      "We don't train shared AI models on your private data without explicit opt-in.",
      "On termination, you can export your data for 30 days. After that we delete it on schedule.",
    ],
  },
  {
    icon: Scale,
    title: "Billing & subscriptions",
    body: [
      "Plans renew automatically until cancelled. You can cancel any time from billing settings.",
      "Refunds for the unused portion of a paid term are pro-rated, except where prohibited by local law.",
      "Taxes are added at checkout where required. Prices are listed in USD unless stated otherwise.",
    ],
  },
  {
    icon: Ban,
    title: "Acceptable use",
    body: [
      "Don't use the platform to discriminate, deceive candidates or violate employment law in your jurisdiction.",
      "Don't reverse-engineer, scrape, or use the service to build competing products.",
      "We may suspend accounts that put the platform, its users or candidates at risk.",
    ],
  },
  {
    icon: AlertTriangle,
    title: "Liability",
    body: [
      "The service is provided \"as is\". We work hard to keep it reliable, but we can't guarantee it'll be perfect.",
      "Our liability is limited to the fees you've paid in the previous 12 months, except for cases where law doesn't permit such limits.",
      "We don't take responsibility for hiring decisions made on the platform, those are yours to make.",
    ],
  },
];

export default function Terms() {
  return (
    <div
      className="relative"
      style={{
        background: C.paper,
        color: C.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* HERO */}
      <section
        className="relative pt-16 pb-16 md:pt-24 md:pb-20 border-b ds-bg-spotlight"
        style={{ borderColor: C.hairline }}
      >
        <PageContainer>
          <motion.div initial="hidden" animate="show" variants={motionFadeUp}>
            <motion.div variants={motionFadeUp} custom={0}>
              <StatusBadge>Terms · last updated June 2026</StatusBadge>
            </motion.div>

            <motion.h1
              variants={motionFadeUp}
              custom={1}
              className="mt-5 text-5xl sm:text-6xl lg:text-[5rem] font-extrabold leading-[0.92] tracking-tight max-w-4xl"
              style={{ color: C.forest }}
            >
              Fair terms.
              <br />
              <span style={{ color: `${C.forest}80` }}>Plainly written.</span>
            </motion.h1>

            <motion.p
              variants={motionFadeUp}
              custom={2}
              className="mt-6 text-lg md:text-xl leading-relaxed max-w-2xl"
              style={{ color: C.grid }}
            >
              The legal bits, condensed. We aim for short, readable terms, if
              anything is unclear, ask us and we'll explain in writing.
            </motion.p>
          </motion.div>
        </PageContainer>
      </section>

      {/* SECTIONS */}
      <section className="py-16 md:py-20 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
            <aside className="lg:col-span-3">
              <div className="sticky top-28">
                <TechLabel style={{ color: `${C.grid}99` }}>contents</TechLabel>
                <ul className="mt-4 space-y-2.5">
                  {sections.map((s, i) => (
                    <li key={s.title}>
                      <a
                        href={`#term-${i + 1}`}
                        className="group flex items-start gap-2 text-sm transition-colors hover:text-[#1A3C2B]"
                        style={{ color: C.grid }}
                      >
                        <span
                          className="text-[10px] font-bold tabular-nums mt-0.5"
                          style={{ letterSpacing: "0.18em", color: C.forest }}
                        >
                          0{i + 1}
                        </span>
                        <span className="group-hover:underline">{s.title}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            <div className="lg:col-span-9 space-y-12">
              {sections.map((s, i) => (
                <motion.section
                  key={s.title}
                  id={`term-${i + 1}`}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.4 }}
                  className="scroll-mt-28"
                >
                  <div className="flex items-center gap-4 mb-5">
                    <div
                      className="w-11 h-11 flex items-center justify-center flex-shrink-0"
                      style={{ background: C.forest, color: C.paper }}
                    >
                      <s.icon className="w-5 h-5" strokeWidth={1.6} />
                    </div>
                    <div>
                      <TechLabel style={{ color: `${C.grid}99` }}>0{i + 1}</TechLabel>
                      <h2 className="text-2xl md:text-3xl font-bold leading-tight" style={{ color: C.forest }}>
                        {s.title}
                      </h2>
                    </div>
                  </div>
                  <div className="space-y-4 text-base leading-relaxed" style={{ color: C.grid }}>
                    {s.body.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                  </div>
                </motion.section>
              ))}
            </div>
          </div>
        </PageContainer>
      </section>

      {/* CONTACT */}
      <section className="py-16 md:py-20" style={{ background: C.forest, color: C.paper }}>
        <PageContainer>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-xl">
              <StatusBadge color={C.mint}>Questions?</StatusBadge>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold leading-[1.05] tracking-tight">
                Email our legal team.
              </h2>
              <p className="mt-3 text-base" style={{ color: "rgba(247,247,245,0.7)" }}>
                We answer questions in plain language, not boilerplate.
              </p>
            </div>
            <div className="flex flex-col gap-2 min-w-0">
              <a
                href="mailto:Info@webidealglobal.com"
                className="inline-flex items-center gap-3 px-5 py-3 border transition-colors hover:bg-[#9EFFBF] hover:text-[#1A3C2B]"
                style={{ borderColor: "rgba(158,255,191,0.4)", color: C.paper }}
              >
                <Mail className="w-4 h-4" />
                <span className="text-sm font-semibold">Info@webidealglobal.com</span>
              </a>
              <Link
                to={createPageUrl("Privacy")}
                className="text-[10px] font-bold uppercase mt-1 hover:text-[#9EFFBF] transition-colors"
                style={{ letterSpacing: "0.18em", color: "rgba(247,247,245,0.55)" }}
              >
                See also · Privacy Policy →
              </Link>
            </div>
          </div>
        </PageContainer>
      </section>
    </div>
  );
}
